import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import TitleDescription from "../../../../components/newMotorPolicy/titleDescription/titleDescription";
import PolicyCard from "../../../../components/policyCard/policyCard";
import useLanguageTranslate from "../../../../hooks/useLanguageTranslate";
import usePolicyCancellationFlow from "../../../../hooks/usePolicyCancellationFlow";
import { selectProfileData } from "../../../../redux/features/profileSlice";
import { getClientSource } from "../../../../utils/getClientSource";
import { formatPremiumWithCurrency } from "../../../../utils/index";
import BottomLayout from "../../../motorInsurancePages/policyOverview/managePolicy/policyCancellationFlow/bottomLayout";
import PolicyCancellationMotorLayout from "../../../motorInsurancePages/policyOverview/managePolicy/policyCancellationFlow/policyCancellationMotorLayout";
import AlertComponent from "../../../motorInsurancePages/transferPolicyFlow/alertComponent";

const SmartDeviceReviewCancellationPage = ({
  handleNavigationToPolicyOverview,
}) => {
  const { handleNextSubstep } = usePolicyCancellationFlow();
  const translate = useLanguageTranslate();
  const navigate = useNavigate();
  const { i18n, t } = useTranslation();

  // Always use English numerals, detect RTL for currency positioning
  const isRTL = i18n.resolvedLanguage === "ar";

  // Get policy detail from Redux (same selector as travel uses)
  const policyDetailsInfo = useSelector(
    (state) => state.policyDetails?.PolicyDetails?.PolicyHeader
  );
  // Get logged in user profile info
  const profileInfo = useSelector(selectProfileData);

  // DETAILED LOGGING FOR BACKEND TEAM
  console.log("=== SMART DEVICE POLICY CANCEL DATA DEBUG ===");
  console.log("Full policyDetailsInfo no json:", policyDetailsInfo);
  console.log(
    "Full policyDetailsInfo:",
    JSON.stringify(policyDetailsInfo, null, 2)
  );
  console.log("FromDateStr:", policyDetailsInfo?.FromDateStr);
  console.log("ToDateStr:", policyDetailsInfo?.ToDateStr);
  console.log("PolicyNumber:", policyDetailsInfo?.PolicyNumber);
  console.log("PlanName:", policyDetailsInfo?.PlanName);
  console.log("ProductCode:", policyDetailsInfo?.ProductCode);
  console.log("Premium:", policyDetailsInfo?.Premium);
  console.log("=== END DEBUG ===");

  // Helper function for RTL-aware currency formatting - always use English numerals
  const formatCurrency = (amount) => {
    const currency = translate("BHD", "د.ب");
    const formattedNumber = formatPremiumWithCurrency(amount, "", "en-US");

    // For RTL, put number first then currency (66.000 د.ب)
    if (isRTL) {
      return (
        <span className="flex items-center gap-1 flex-row-reverse">
          <span>{currency}</span>
          <span>{formattedNumber}</span>
        </span>
      );
    }
    // For LTR, currency before number
    return `${currency} ${formattedNumber}`;
  };

  // Helper function to format date to English format (10 July 2025)
  const formatDateToEnglish = (dateStr) => {
    if (!dateStr) return "--";

    try {
      const parts = dateStr.split("/");
      const date = new Date(parts[2], parts[1] - 1, parts[0]);
      return date.toLocaleDateString("en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric",
      });
    } catch {
      return dateStr;
    }
  };

  // Helper function to format dates as DD/MM/YYYY (same as policy card)
  const formatDateAsNumbers = (dateStr) => {
    if (!dateStr) return "N/A";

    try {
      let date;

      // Handle different date formats
      if (typeof dateStr === "string" && dateStr.includes("/")) {
        // Already in DD/MM/YYYY format
        const parts = dateStr.split("/");
        if (parts.length === 3) {
          return `${parts[0].padStart(2, "0")}/${parts[1].padStart(2, "0")}/${
            parts[2]
          }`;
        }
      }

      // Check if it's a unix timestamp (number or string number)
      const timestamp =
        typeof dateStr === "string" ? parseInt(dateStr) : dateStr;
      if (!isNaN(timestamp) && timestamp > 1000000000) {
        // Unix timestamp in seconds, convert to milliseconds
        date = new Date(timestamp * 1000);
      } else {
        // Try parsing as regular date
        date = new Date(dateStr);
      }

      if (isNaN(date.getTime())) {
        return dateStr; // Return original if can't parse
      }

      const day = date.getDate().toString().padStart(2, "0");
      const month = (date.getMonth() + 1).toString().padStart(2, "0");
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch {
      return dateStr || "N/A";
    }
  };

  // Create smart device policy card info (adapting motor structure for smart device)
  const policyCardInfo = {
    ModelYear: "",
    Make: "Smart Device Insurance", // Smart Device Policy
    Model: policyDetailsInfo?.DeviceTypeName || policyDetailsInfo?.DeviceType || policyDetailsInfo?.DeviceName || "Device Protection", // Device type
    CPR: "",
    PolicyNumber: policyDetailsInfo?.PolicyNumber || "--",
    RegistrationNumber: "", // Not applicable for smart device
    SumInsured: 0.0,
    PlanName: policyDetailsInfo?.PlanName || "--",
    ProductCode: "0213", // Smart Device insurance product code
    ProductName: "Smart Device",
    StartDate:
      policyDetailsInfo?.FromDateStr?.split(" ")[0]
        ?.split("/")
        ?.reverse()
        ?.join("-") || "--",
    EndDate:
      policyDetailsInfo?.ToDateStr?.split(" ")[0]
        ?.split("/")
        ?.reverse()
        ?.join("-") || "--",
    FromDateStr: policyDetailsInfo?.FromDateStr?.split(" ")[0] || "--",
    ToDateStr: policyDetailsInfo?.ToDateStr?.split(" ")[0] || "--",
    Branch: getClientSource(),
    MainChannel: "Direct Bahrain",
    Renewed: policyDetailsInfo?.IsRenewed,
    PolicyStatus: "Active",
    IsRenewable: false,
    // Add these fields for NonMotorPolicyCard to display correctly
    DeviceTypeName: policyDetailsInfo?.DeviceTypeName,
    DeviceType: policyDetailsInfo?.DeviceType,
    DeviceName: policyDetailsInfo?.DeviceName,
  };

  /* Next */
  const continueClickHandler = () => {
    handleNextSubstep();
  };

  return (
    <PolicyCancellationMotorLayout
      is_hide_back_btn={false}
      isFirstStep={true}
      handleCloseBtnClick={handleNavigationToPolicyOverview}
      btn_onClick={continueClickHandler}
      btn_disabled={false}
      btn_label={translate("Continue", "التالي")}
      CancellationAdditionalComponent={
        <BottomLayout
          className="btvk"
          btn_onClick={continueClickHandler}
          btn_disabled={false}
          btn_label={translate("Continue", "التالي")}
          handleNavigationToPolicyOverview={handleNavigationToPolicyOverview}
        />
      }
    >
      <div className="flex flex-col items-center mx-auto max-w-lg max767:p-5 max767:mt-0 max767:pt-0 max767:pb-[200px] pt-[1%] pb-[160px]">
        <div className="flex justify-center items-center w-full">
          <div className=" p-0 w-full ">
            <TitleDescription
              className="max767:mt-0!important"
              title={translate("Policy Cancellation", "تسليم سياستك")}
              description={translate(
                "Please review your smart device policy details before proceeding with cancellation.",
                "قم بمراجعة تفاصيل سياستك وقم بتوفير المعلومات اللازمة للمضي قدمًا في عملية الاستسلام."
              )}
            />
            <AlertComponent
              className="mb-4"
              title={translate(
                "Note that cancelling your policy is a request and is subject to approval based on our cancellation policy. Ensure you understand the implications of this action.",
                "يرجى ملاحظة أن هذا الطلب سيتم تقديمه للموافقة عليه قبل أن يصبح ساري المفعول"
              )}
            />
            <div className="flex flex-col justify-center items-center mt-5 bg-[#F6F8F9] p-4 rounded-2xl max767:bg-white max767:p-0">
              <PolicyCard
                className="mb-4"
                policy_name="Smart Device Insurance"
                policy_label={policyDetailsInfo?.DeviceTypeName || policyDetailsInfo?.DeviceType || policyDetailsInfo?.DeviceName || "Smart Device Policy"}
                policy_number={policyDetailsInfo?.PolicyNumber || "N/A"}
                from_date={policyCardInfo.FromDateStr}
                to_date={policyCardInfo.ToDateStr}
                policy_bg_image={policyCardInfo.policy_bg_image}
                expire_days={policyCardInfo.expire_days}
                is_text_dark={policyCardInfo.is_text_dark}
                mapping_key={policyCardInfo.id}
                data={policyCardInfo}
                disableCardClick
              />

              {/* Smart Device specific table - showing required fields */}
              {/* Use grid layout for Arabic, table for English */}
              {isRTL ? (
                <div className="w-full bg-white border border-solid border-[#DDE2E4] rounded-[10px] overflow-hidden">
                  {/* 1. Full Name */}
                  <div className="grid grid-cols-2 border-b border-[#DDE2E4]">
                    <div className="px-4 border-l border-[#DDE2E4] text-[#5B6871] text-sm font-medium h-[44px] leading-[44px]">
                      {translate("Full Name", "الاسم الكامل")}
                    </div>
                    <div className="py-2 px-4 text-[#1A2024] text-sm font-medium leading-6">
                      {profileInfo?.FirstName && profileInfo?.LastName
                        ? `${profileInfo.FirstName} ${profileInfo.LastName}`
                        : translate("N/A", "غير متوفر")}
                    </div>
                  </div>

                  {/* 2. CPR */}
                  <div className="grid grid-cols-2 border-b border-[#DDE2E4]">
                    <div className="px-4 border-l border-[#DDE2E4] text-[#5B6871] text-sm font-medium h-[44px] leading-[44px]">
                      {translate("CPR", "الرقم الشخصي")}
                    </div>
                    <div className="py-2 px-4 text-[#1A2024] text-sm font-medium leading-6">
                      {profileInfo?.CPR || translate("N/A", "غير متوفر")}
                    </div>
                  </div>

                  {/* 3. Device's IMEI */}
                  <div className="grid grid-cols-2 border-b border-[#DDE2E4]">
                    <div className="px-4 border-l border-[#DDE2E4] text-[#5B6871] text-sm font-medium h-[44px] leading-[44px]">
                      {translate("Device's IMEI", "رقم IMEI للجهاز")}
                    </div>
                    <div className="py-2 px-4 text-[#1A2024] text-sm font-medium leading-6">
                      {policyDetailsInfo?.IMEI ||
                        policyDetailsInfo?.IMEINumber ||
                        policyDetailsInfo?.DeviceIMEI ||
                        translate("N/A", "غير متوفر")}
                    </div>
                  </div>

                  {/* 4. Brand */}
                  <div className="grid grid-cols-2 border-b border-[#DDE2E4]">
                    <div className="px-4 border-l border-[#DDE2E4] text-[#5B6871] text-sm font-medium h-[44px] leading-[44px]">
                      {translate("Brand", "العلامة التجارية")}
                    </div>
                    <div className="py-2 px-4 text-[#1A2024] text-sm font-medium leading-6">
                      {policyDetailsInfo?.Brand ||
                        policyDetailsInfo?.Make ||
                        translate("N/A", "غير متوفر")}
                    </div>
                  </div>

                  {/* 5. Model - Show DeviceTypeName */}
                  <div className="grid grid-cols-2 border-b border-[#DDE2E4]">
                    <div className="px-4 border-l border-[#DDE2E4] text-[#5B6871] text-sm font-medium h-[44px] leading-[44px]">
                      {translate("Model", "الموديل")}
                    </div>
                    <div className="py-2 px-4 text-[#1A2024] text-sm font-medium leading-6">
                      {policyDetailsInfo?.DeviceTypeName ||
                        policyDetailsInfo?.DeviceType ||
                        policyDetailsInfo?.DeviceName ||
                        policyDetailsInfo?.Model ||
                        policyDetailsInfo?.DeviceModel ||
                        translate("N/A", "غير متوفر")}
                    </div>
                  </div>

                  {/* 6. Device Price - formatted like coverage amount */}
                  <div className="grid grid-cols-2 border-b border-[#DDE2E4]">
                    <div className="px-4 border-l border-[#DDE2E4] text-[#5B6871] text-sm font-medium h-[44px] leading-[44px]">
                      {translate("Device Price", "سعر الجهاز")}
                    </div>
                    <div className="py-2 px-4 text-[#1A2024] text-sm font-medium leading-6 flex justify-end rtl:justify-start">
                      {formatCurrency(
                        policyDetailsInfo?.DevicePrice ||
                          policyDetailsInfo?.SumInsured
                      )}
                    </div>
                  </div>

                  {/* 7. Purchase Date */}
                  <div className="grid grid-cols-2 border-b border-[#DDE2E4]">
                    <div className="px-4 border-l border-[#DDE2E4] text-[#5B6871] text-sm font-medium h-[44px] leading-[44px]">
                      {translate("Purchase Date", "تاريخ الشراء")}
                    </div>
                    <div className="py-2 px-4 text-[#1A2024] text-sm font-medium leading-6">
                      {policyDetailsInfo?.PurchaseDate
                        ? formatDateAsNumbers(policyDetailsInfo.PurchaseDate)
                        : translate("N/A", "غير متوفر")}
                    </div>
                  </div>
                </div>
              ) : (
                <table className="min-w-full border-separate bg-white border border-solid border-[#DDE2E4] border-spacing-0 rounded-[10px]">
                  <tbody>
                    {/* 1. Full Name - from logged in user */}
                    <tr>
                      <td className="h-[44px] leading-[44px] py-0 px-4 border-b border-r text-[#5B6871] text-sm font-medium leading-6">
                        {translate("Full Name", "الاسم الكامل")}
                      </td>
                      <td className="py-0 px-4 border-b text-[#1A2024] text-sm font-medium leading-6">
                        {profileInfo?.FirstName && profileInfo?.LastName
                          ? `${profileInfo.FirstName} ${profileInfo.LastName}`
                          : translate("N/A", "غير متوفر")}
                      </td>
                    </tr>

                    {/* 2. CPR - from logged in user */}
                    <tr>
                      <td className="h-[44px] leading-[44px] py-0 px-4 border-b border-r text-[#5B6871] text-sm font-medium leading-6">
                        {translate("CPR", "الرقم الشخصي")}
                      </td>
                      <td className="py-0 px-4 border-b text-[#1A2024] text-sm font-medium leading-6">
                        {profileInfo?.CPR || translate("N/A", "غير متوفر")}
                      </td>
                    </tr>

                    {/* 3. Device's IMEI */}
                    <tr>
                      <td className="h-[44px] leading-[44px] py-0 px-4 border-b border-r text-[#5B6871] text-sm font-medium leading-6">
                        {translate("Device's IMEI", "رقم IMEI للجهاز")}
                      </td>
                      <td className="py-0 px-4 border-b text-[#1A2024] text-sm font-medium leading-6">
                        {policyDetailsInfo?.IMEI ||
                          policyDetailsInfo?.IMEINumber ||
                          policyDetailsInfo?.DeviceIMEI ||
                          translate("N/A", "غير متوفر")}
                      </td>
                    </tr>

                    {/* 4. Brand */}
                    <tr>
                      <td className="h-[44px] leading-[44px] py-0 px-4 border-b border-r text-[#5B6871] text-sm font-medium leading-6">
                        {translate("Brand", "العلامة التجارية")}
                      </td>
                      <td className="py-0 px-4 border-b text-[#1A2024] text-sm font-medium leading-6">
                        {policyDetailsInfo?.Brand ||
                          policyDetailsInfo?.Make ||
                          translate("N/A", "غير متوفر")}
                      </td>
                    </tr>

                    {/* 5. Model - Show DeviceTypeName */}
                    <tr>
                      <td className="h-[44px] leading-[44px] py-0 px-4 border-b border-r text-[#5B6871] text-sm font-medium leading-6">
                        {translate("Model", "الموديل")}
                      </td>
                      <td className="py-0 px-4 border-b text-[#1A2024] text-sm font-medium leading-6">
                        {policyDetailsInfo?.DeviceTypeName ||
                          policyDetailsInfo?.DeviceType ||
                          policyDetailsInfo?.DeviceName ||
                          policyDetailsInfo?.Model ||
                          policyDetailsInfo?.DeviceModel ||
                          translate("N/A", "غير متوفر")}
                      </td>
                    </tr>

                    {/* 6. Device Price - formatted like coverage amount */}
                    <tr>
                      <td className="h-[44px] leading-[44px] py-0 px-4 border-b border-r text-[#5B6871] text-sm font-medium leading-6">
                        {translate("Device Price", "سعر الجهاز")}
                      </td>
                      <td className="py-0 px-4 border-b text-[#1A2024] text-sm font-medium leading-6">
                        {formatCurrency(
                          policyDetailsInfo?.DevicePrice ||
                            policyDetailsInfo?.SumInsured
                        )}
                      </td>
                    </tr>

                    {/* 7. Purchase Date */}
                    <tr>
                      <td className="py-2 px-4 border-r text-[#5B6871] text-sm font-medium leading-6">
                        {translate("Purchase Date", "تاريخ الشراء")}
                      </td>
                      <td className="py-2 px-4 text-[#1A2024] text-sm font-medium leading-6">
                        {policyDetailsInfo?.PurchaseDate
                          ? formatDateAsNumbers(policyDetailsInfo.PurchaseDate)
                          : translate("N/A", "غير متوفر")}
                      </td>
                    </tr>
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </div>
    </PolicyCancellationMotorLayout>
  );
};

export default SmartDeviceReviewCancellationPage;
